import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo, testimonialList } from "@/mocks/site";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "只梳理，不定性",
    text: "我们只做公开信息的整理与呈现，不对事件本身做事实认定，也不传播未经证实的说法。",
  },
  {
    icon: "ri-time-line",
    title: "持续跟进",
    text: "热点会发展，结论也会变化。出现新进展时，我们会回到原条目上补充，而不是另起一条重复内容。",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "克制表达",
    text: "吃瓜可以轻松，但不应该伤人。涉及个人隐私与未公开信息的内容，我们一律不收录。",
  },
  {
    icon: "ri-refresh-line",
    title: "及时更正",
    text: "如果发现已经发布的内容与后续事实不符，我们会在原页面标注更正说明，而不是悄悄删掉。",
  },
];

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      name: "关于我们 - 热搜吃瓜",
      url: "https://fvwyz.cn/about",
      inLanguage: "zh-CN",
      description: "了解热搜吃瓜的定位、内容原则与团队理念。",
    },
    {
      "@type": "Organization",
      name: siteInfo.name,
      url: "https://fvwyz.cn/",
      email: siteInfo.email,
      description: siteInfo.description,
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://fvwyz.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://fvwyz.cn/about" },
      ],
    },
  ],
};

export default function About() {
  return (
    <SiteLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }} />

      <section className="relative w-full overflow-hidden border-b border-background-200">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20editorial%20desk%20scene%20with%20soft%20morning%20light%20and%20abstract%20amber%20shapes%20on%20cream%20background%20minimal%20magazine%20visual%20calm%20and%20clean&width=1600&height=600&seq=gossip-about-01&orientation=landscape"
            alt="热搜吃瓜团队内容工作场景抽象视觉"
            title="关于热搜吃瓜"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/70 to-foreground-950/40"></div>
        </div>
        <div className="relative mx-auto w-full px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              关于热搜吃瓜
            </h1>
            <p className="mt-5 text-sm leading-relaxed text-background-200 md:text-base">
              {siteInfo.slogan}。我们想做的，是把散落在各个平台上的热点线索集中到一处，让你花更少的时间，掌握更完整的来龙去脉。
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们是谁</h2>
            <div className="mt-4 space-y-4 text-base leading-loose text-foreground-700">
              <p>
                {siteInfo.name}是一个专注于当日热点与娱乐动态的内容门户。团队规模不大，但每天的更新从清晨持续到深夜，目的只有一个：让打开首页的人不用来回切换应用，也能知道此刻大家都在聊什么。
              </p>
              <p>
                我们相信「快」和「准」同样重要。热点会在几个小时内被讨论、被澄清、被反转，所以我们不急于给出结论，而是先把时间线和各方说法摆清楚，让读者自己判断。
              </p>
              <p>
                网站不设会员、不做弹窗，也不靠制造情绪换取点击。内容的排序只参考讨论热度，不参考商业合作。这一点，从第一天到现在都没有变过。
              </p>
            </div>

            <h2 className="mt-12 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们的内容原则</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">网站信息</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">网站定位</dt>
                  <dd className="font-medium text-foreground-900">热点内容门户</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">内容方向</dt>
                  <dd className="font-medium text-foreground-900">娱乐 · 综艺 · 影视</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">更新频率</dt>
                  <dd className="font-medium text-foreground-900">每小时滚动更新</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">最近更新</dt>
                  <dd className="font-medium text-foreground-900">{siteInfo.updatedAt}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h2 className="font-heading text-base font-bold text-secondary-900">联系我们</h2>
              <p className="mt-2 text-sm leading-relaxed text-secondary-800">
                内容纠错、线索提供或合作咨询，都可以通过下面的方式找到我们。
              </p>
              <div className="mt-4 space-y-3 text-sm text-secondary-900">
                <a href={`mailto:${siteInfo.email}`} className="flex items-center gap-2 transition-colors hover:text-secondary-600">
                  <span className="flex h-5 w-5 items-center justify-center">
                    <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                  </span>
                  {siteInfo.email}
                </a>
                <span className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center">
                    <i className="ri-weibo-line w-5 h-5 flex items-center justify-center"></i>
                  </span>
                  {siteInfo.weibo}
                </span>
                <span className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center">
                    <i className="ri-wechat-line w-5 h-5 flex items-center justify-center"></i>
                  </span>
                  公众号：{siteInfo.wechat}
                </span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            下面这些反馈来自日常浏览我们的读者，也是我们持续更新下去的动力。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {testimonialList.map((item) => (
              <figure key={item.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-double-quotes-l w-5 h-5 flex items-center justify-center"></i>
                </span>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.content}</blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-4">
                  <span className="block text-sm font-semibold text-foreground-950">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <div className="flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 px-6 py-10 text-center">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">现在就去看看今天发生了什么</h2>
          <p className="max-w-xl text-sm leading-relaxed text-foreground-600">
            首页实时汇总了当前讨论度最高的话题，从榜单到详情，一次看清事件脉络。
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/rankings"
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              查看实时榜单
            </Link>
            <Link
              to="/articles"
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              浏览全部资讯
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}